// Mission Control — the founder's command bridge.
//
// This is the first surface a founder sees after signing in. It is NOT
// another analytics dashboard: no charts, no giant tables. The purpose is
// to answer "what is the state of ReVloom right now, and what needs me
// right now" within ~30 seconds of glancing.
//
// Data comes from a single admin-gated endpoint (`founder-mission-control`)
// that reads pre-aggregated summary tables plus a small live-hour agg,
// runs the deterministic executive-team briefs, and caches the briefs into
// founder_ops.agent_reports. See docs/founder-ops-architecture.md.

import { Component, useCallback, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { supabase } from '../supabase';
import { useSession } from '../auth/useSession';
import { captureError } from '../sentry';
import { PageHeader } from '../components/Layout';
import { Button, Card } from '../components/ui';
import { COLORS, RADIUS } from '../theme';
import { num, relativeTime } from '../lib/format';
import FounderOpsTimeline, { severityColor as busSeverityColor } from '../components/FounderOpsTimeline';
import type {
  FounderOpsEvent,
  FounderOpsEventFilter,
} from '../lib/founderOpsEvents';

// ── Response shapes ────────────────────────────────────────────────────────

type IncidentState = 'none' | 'watch' | 'candidate' | 'critical_candidate';

interface Brief {
  id:            string;
  name:          string;
  title:         string;
  severity:      'healthy' | 'watch' | 'needs_attention' | 'critical' | 'no_data';
  severityLabel: string;
  score:         number | null;
  summary:       string;
  risk:          string;
  action:        string;
  confidencePct: number;
  evidence:      string[];
  // Phase 4B extensions
  situation?:      string;
  likelyCause?:    string;
  riskIfIgnored?:  string;
  nextCheckpoint?: string;
  incidentState?:  IncidentState;
}

interface AvaAction {
  title: string;
  reason: string;
  urgency: 'now' | 'today' | 'this_week' | 'later';
  estimatedEffort: string;
  confidence: number;
}

interface AvaBriefResponse {
  briefTitle: string;
  overallStatus: string;
  readingTimeSeconds: number;
  summary: string;
  immediateAttention: string[];
  todayWins: string[];
  risks: string[];
  recommendedActions: AvaAction[];
  unknowns: string[];
  confidence: number;
  generatedAt: string;
  model: string;
  costMicroUsd: number;
  runsRemainingToday?: number | null;
  memory_touched?: { timeline: number; journal: number; lessons: number; priorReports: number } | null;
  // 429 patch: server may return a cached brief instead of generating one
  // to avoid consuming a rate-limit slot on page-load auto-triggers.
  cached?:             boolean;
  cached_age_seconds?: number;
}

type AvaBriefState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready';   brief: AvaBriefResponse }
  | { status: 'error';   message: string };

type EventsState =
  | { status: 'loading' }
  | { status: 'ready';  events: FounderOpsEvent[] }
  | { status: 'error';  message: string };

// Phase 6I — Archivist health envelope from `founder-archivist-health`.
// Kept narrow: only the fields we render, so a future addition to the
// endpoint doesn't force a mirror update here.
type ArchivistHealthLevel = 'healthy' | 'watch' | 'degraded' | 'unhealthy' | 'unknown';

interface ArchivistHealthPayload {
  generated_at:              string;
  health:                    ArchivistHealthLevel;
  latest_check_severity:     string | null;
  latest_check_age_seconds:  number | null;
  summary_counts?: {
    missing_daily_narratives?: number;
    missing_facts_extraction?: number;
    failed_archivist_reports?: number;
    stale_cron_runs?:          number;
  };
  open_journal_count?:       number;
  duration_ms?:              number;
}

type ArchivistHealthState =
  | { status: 'loading' }
  | { status: 'ready';   payload: ArchivistHealthPayload }
  | { status: 'error';   message: string };

interface TimelineEntry {
  id:          string;
  occurred_at: string;
  category:    string;
  severity:    string;
  title:       string;
  summary:     string | null;
}

interface Snapshot {
  generated_at:            string;
  today:                   Record<string, number>;
  yesterday_daily:         Record<string, unknown> | null;
  recent_24h:              { critical_events: number; warning_events: number };
  last_google_sync:        string | null;
  last_deployment:         string | null;
  timeline_recent:         TimelineEntry[];
  open_journal_count:      number;
  active_incidents_count:  number;
}

interface CacheOutcome {
  agent:     string;
  reused:    boolean;
  report_id: string | null;
}

interface MissionControlPayload {
  snapshot:         Snapshot;
  briefs:           Brief[];
  executive_cache:  { metrics_hash: string; per_agent: CacheOutcome[] };
  served_from:      string;
  duration_ms:      number;
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function MissionControlPage() {
  const { session } = useSession();
  const [payload, setPayload] = useState<MissionControlPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ava brief is fetched independently: Mission Control must remain usable
  // even if the AI endpoint is misconfigured, rate-limited, or offline.
  const [ava, setAva] = useState<AvaBriefState>({ status: 'idle' });
  const [askOpen, setAskOpen] = useState(false);
  const [askQuestion, setAskQuestion] = useState('');

  // Founder Ops event bus — fetched separately so a slow / failing bus
  // read never blocks the primary Mission Control snapshot.
  const [eventsState, setEventsState] = useState<EventsState>({ status: 'loading' });

  // Phase 6I — Archivist health is fetched independently so a memory-system
  // outage never blocks Mission Control's primary load.
  const [archivistHealth, setArchivistHealth] = useState<ArchivistHealthState>({ status: 'loading' });

  const load = useCallback(async () => {
    setError(null);
    const { data, error } = await supabase.functions.invoke<MissionControlPayload>(
      'founder-mission-control', { body: {} },
    );
    if (error || !data) {
      setError(error?.message ?? 'Failed to load Mission Control.');
    } else {
      setPayload(data);
    }
    setLoading(false);
  }, []);

  const loadAva = useCallback(async (question?: string, mission?: MissionControlPayload) => {
    setAva({ status: 'loading' });
    const source = mission ?? payload;
    const { data, error } = await supabase.functions.invoke<AvaBriefResponse>('founder-ava-brief', {
      body: {
        question:       question?.trim() || undefined,
        missionControl: source?.snapshot,
        briefs:         source?.briefs,
      },
    });
    if (error || !data) {
      // supabase-js surfaces non-2xx responses as FunctionsHttpError whose
      // .context is the raw Response. Read status so we can render a
      // meaningful message for the 3/day rate limit instead of the generic
      // "Edge Function returned a non-2xx status code" default.
      const status = (error as { context?: Response } | null)?.context?.status;
      const message = status === 429
        ? 'Ava brief limit reached. Try again later or use the latest cached brief.'
        : (error?.message ?? 'Ava brief unavailable.');
      setAva({ status: 'error', message });
      return;
    }
    setAva({ status: 'ready', brief: data });
  }, [payload]);

  const loadEvents = useCallback(async () => {
    setEventsState({ status: 'loading' });
    const filter: FounderOpsEventFilter = { limit: 25 };
    const { data, error } = await supabase.functions.invoke<{ events: FounderOpsEvent[] }>(
      'founder-events-timeline', { body: filter },
    );
    if (error || !data) {
      setEventsState({ status: 'error', message: error?.message ?? 'Failed to load events.' });
      return;
    }
    setEventsState({ status: 'ready', events: data.events ?? [] });
  }, []);

  const loadArchivistHealth = useCallback(async () => {
    setArchivistHealth({ status: 'loading' });
    const { data, error } = await supabase.functions.invoke<ArchivistHealthPayload>(
      'founder-archivist-health', { body: {} },
    );
    if (error || !data) {
      setArchivistHealth({
        status:  'error',
        message: error?.message ?? 'Archivist health endpoint unavailable.',
      });
      return;
    }
    setArchivistHealth({ status: 'ready', payload: data });
  }, []);

  useEffect(() => {
    void load();
    void loadEvents();
    void loadArchivistHealth();
  }, [load, loadEvents, loadArchivistHealth]);
  // Kick off Ava as soon as the deterministic payload lands. If the payload
  // fails, we don't attempt Ava — she needs the same data anyway.
  useEffect(() => {
    if (payload && ava.status === 'idle') void loadAva(undefined, payload);
  }, [payload, ava.status, loadAva]);

  if (loading) return <div style={loadingStyle}>Loading Mission Control…</div>;
  if (error)   return <div style={errorStyle}>{error} <Button onClick={() => { setLoading(true); void load(); }}>Retry</Button></div>;
  if (!payload) return null;

  const avaDeterministic = payload.briefs.find((b) => b.id === 'ava');
  const watchtower       = payload.briefs.find((b) => b.id === 'watchtower');
  const peers            = payload.briefs.filter((b) => b.id !== 'ava');
  const overall          = avaDeterministic?.score ?? 0;
  const overallColor     = severityColor(avaDeterministic?.severity ?? 'no_data');
  const firstName        = (session?.user?.email ?? '').split('@')[0].split('.')[0];
  const greeting         = getGreeting();
  const incidentState    = watchtower?.incidentState ?? 'none';

  const critical  = peers.filter((b) => b.severity === 'critical' || b.severity === 'needs_attention');
  const winsToday = collectWins(payload.snapshot, peers);
  const actions   = collectActions(peers);
  const cacheHits = payload.executive_cache.per_agent.filter((p) => p.reused).length;

  return (
    <div>
      <PageHeader
        title="Mission Control"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
              Generated {relativeTime(payload.snapshot.generated_at)}
              {cacheHits > 0 ? ` · ${cacheHits}/${payload.executive_cache.per_agent.length} briefs from cache` : ''}
            </span>
            <Button variant="ghost" onClick={() => { setLoading(true); void load(); }}>Refresh</Button>
          </div>
        }
      />

      {/* ── Ava brief (Phase 4A / 4C) ─────────────────────────────────── */}
      <AvaErrorBoundary>
        <AvaBriefCard
          state={ava}
          onRefresh={() => { void loadAva(undefined); }}
          onAsk={() => setAskOpen(true)}
        />
      </AvaErrorBoundary>

      {/* ── Ask Ava modal ─────────────────────────────────────────────── */}
      {askOpen && (
        <AskAvaModal
          value={askQuestion}
          onChange={setAskQuestion}
          onSubmit={() => {
            const q = askQuestion.trim();
            setAskOpen(false);
            void loadAva(q);
            setAskQuestion('');
          }}
          onCancel={() => setAskOpen(false)}
        />
      )}

      {/* ── Greeting + overall health ──────────────────────────────────── */}
      <Card style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32 }}>
          <div>
            <div style={{ color: COLORS.textMuted, fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6 }}>
              {greeting}
            </div>
            <div style={{ color: COLORS.text, fontSize: 30, fontWeight: 600, letterSpacing: -0.5, marginBottom: 12 }}>
              {firstName ? `Good ${greetingTime()}, ${capitalise(firstName)}.` : `Good ${greetingTime()}.`}
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: 15, lineHeight: 1.5 }}>
              {avaDeterministic?.summary ?? 'Executive briefing not available yet.'}
            </div>
            <div style={{ marginTop: 12 }}>
              <IncidentStatePill state={incidentState} />
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase' }}>Overall</div>
            <div style={{ color: overallColor, fontSize: 56, fontWeight: 700, letterSpacing: -1.5, marginTop: 4 }}>{overall}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 11 }}>/ 100</div>
          </div>
        </div>
      </Card>

      {/* ── Immediate attention ───────────────────────────────────────── */}
      <SectionBlock title="Immediate Attention" accent={critical.length > 0 ? COLORS.critical : COLORS.textMuted}>
        {critical.length === 0 ? (
          <Empty>No critical items. Nothing needs the founder right now.</Empty>
        ) : (
          <ul style={listStyle}>
            {critical.map((b) => (
              <li key={b.id} style={listItemStyle}>
                <span style={{ ...dotStyle, background: severityColor(b.severity) }} />
                <span style={{ color: COLORS.text }}>
                  <b>{b.name}:</b> {b.risk}
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionBlock>

      {/* ── Today's wins ──────────────────────────────────────────────── */}
      <SectionBlock title="Today's Wins" accent={COLORS.success}>
        {winsToday.length === 0 ? (
          <Empty>No wins recorded yet today. Keep shipping.</Empty>
        ) : (
          <ul style={listStyle}>
            {winsToday.map((w, i) => (
              <li key={i} style={listItemStyle}>
                <span style={{ ...dotStyle, background: COLORS.success }} />
                <span style={{ color: COLORS.text }}>{w}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionBlock>

      {/* ── Recommended actions ──────────────────────────────────────── */}
      <SectionBlock title="Recommended Actions" accent={COLORS.accent}>
        {actions.length === 0 ? (
          <Empty>No prioritised actions today.</Empty>
        ) : (
          <ol style={{ ...listStyle, paddingLeft: 0 }}>
            {actions.slice(0, 3).map((a) => (
              <li key={a.agent} style={listItemStyle}>
                <span style={{ ...dotStyle, background: COLORS.accent }} />
                <span style={{ color: COLORS.text }}>
                  <b>{a.name}:</b> {a.action}
                </span>
              </li>
            ))}
          </ol>
        )}
      </SectionBlock>

      {/* ── Timeline ─────────────────────────────────────────────────── */}
      <SectionBlock title="Latest Timeline" accent={COLORS.info}>
        {payload.snapshot.timeline_recent.length === 0 ? (
          <Empty>No timeline entries yet. The promotion job will surface milestones as they occur.</Empty>
        ) : (
          <div>
            {payload.snapshot.timeline_recent.map((t) => (
              <div key={t.id} style={timelineRowStyle}>
                <div style={{ minWidth: 90, color: COLORS.textMuted, fontSize: 11 }}>{relativeTime(t.occurred_at)}</div>
                <div style={{ ...dotStyle, background: severityFromString(t.severity), marginRight: 8 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 500 }}>{t.title}</div>
                  {t.summary && <div style={{ color: COLORS.textSecondary, fontSize: 12, lineHeight: 1.5, marginTop: 2 }}>{t.summary}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionBlock>

      {/* ── System status ────────────────────────────────────────────── */}
      <SectionBlock title="System Status" accent={COLORS.textMuted}>
        <div style={statusGrid}>
          {computeSystemStatus(payload).map((s) => (
            <div key={s.system} style={statusPillStyle}>
              <div style={{ ...dotStyle, background: s.color, marginRight: 8 }} />
              <div style={{ flex: 1 }}>
                <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 500 }}>{s.system}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11 }}>{s.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionBlock>

      {/* ── Executive team ───────────────────────────────────────────── */}
      <SectionBlock title="Executive Team" accent={COLORS.textMuted}>
        <div style={teamGrid}>
          {payload.briefs.map((b) => (
            <div key={b.id} style={{ ...teamCardStyle, borderColor: severityColor(b.severity) + '55' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div>
                  <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600 }}>{b.name}</div>
                  <div style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', marginTop: 2 }}>
                    {b.title}
                  </div>
                </div>
                <div style={{ color: severityColor(b.severity), fontSize: 20, fontWeight: 600 }}>
                  {b.score ?? '—'}
                </div>
              </div>
              <div style={{ color: COLORS.textSecondary, fontSize: 12, lineHeight: 1.5, marginTop: 8 }}>
                {b.summary}
              </div>
            </div>
          ))}
        </div>
      </SectionBlock>

      {/* ── Event-bus-backed cards (Phase 5) ─────────────────────────── */}
      <EventBusCards state={eventsState} onRefresh={() => { void loadEvents(); }} />

      {/* ── Archivist health (Phase 6I) ─────────────────────────────── */}
      <ArchivistHealthCard state={archivistHealth} onRefresh={loadArchivistHealth} />

      {/* ── Footer: activity counters ────────────────────────────────── */}
      <div style={footerRowStyle}>
        <span>Active incidents: <b style={{ color: payload.snapshot.active_incidents_count > 0 ? COLORS.warn : COLORS.text }}>{num(payload.snapshot.active_incidents_count)}</b></span>
        <span>Open journal entries: <b style={{ color: COLORS.text }}>{num(payload.snapshot.open_journal_count)}</b></span>
        <span>Critical events (24h): <b style={{ color: payload.snapshot.recent_24h.critical_events > 0 ? COLORS.critical : COLORS.text }}>{num(payload.snapshot.recent_24h.critical_events)}</b></span>
        <span>Cache hits: <b style={{ color: COLORS.text }}>{cacheHits}/{payload.executive_cache.per_agent.length}</b></span>
        <span>Response time: <b style={{ color: COLORS.text }}>{payload.duration_ms}ms</b></span>
      </div>
    </div>
  );
}

// ── Event-bus-backed cards (Phase 5) ───────────────────────────────────────

function EventBusCards({ state, onRefresh }: { state: EventsState; onRefresh: () => void }) {
  const events = state.status === 'ready' ? state.events : [];

  // Latest per targeted concept. Deterministic: pick the most recent event
  // matching a category/type predicate, or null.
  const latestDeployment  = pickLatest(events, (e) => e.category === 'deployment');
  const googleLatest      = pickLatest(events, (e) => e.category === 'google');
  const latestSecurity    = pickLatest(events, (e) => e.category === 'security');
  const latestAvaBriefEv  = pickLatest(events, (e) => e.type === 'ava_brief_created');

  return (
    <>
      <div style={eventCardsGrid}>
        <EventSingleCard title="Latest Deployment"      event={latestDeployment} />
        <EventSingleCard title="Google API Status"      event={googleLatest} statusHint={googleStatusHint(googleLatest)} />
        <EventSingleCard title="Latest Security Signal" event={latestSecurity} />
        <EventSingleCard title="Latest Ava Brief"       event={latestAvaBriefEv} />
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 10 }}>
          <span style={{ width: 3, height: 16, background: COLORS.textMuted, borderRadius: 2 }} />
          <span style={{ color: COLORS.text, fontSize: 16, fontWeight: 600 }}>Recent Operations Timeline</span>
          <span style={{ flex: 1 }} />
          <Button variant="ghost" onClick={onRefresh}>Refresh events</Button>
        </div>
        <Card>
          <FounderOpsTimeline
            events={events}
            loading={state.status === 'loading'}
            error={state.status === 'error' ? state.message : null}
            emptyText="No events on the bus yet. Run `supabase db reset` locally to seed representative events."
          />
        </Card>
      </div>
    </>
  );
}

function EventSingleCard({
  title, event, statusHint,
}: {
  title: string;
  event: FounderOpsEvent | null;
  statusHint?: string;
}) {
  return (
    <div style={eventSingleCardStyle}>
      <div style={eventCardHeader}>
        <span style={eventCardHeaderTitle}>{title}</span>
        {statusHint && <span style={{ color: COLORS.textMuted, fontSize: 10 }}>{statusHint}</span>}
      </div>
      {!event ? (
        <div style={{ color: COLORS.textMuted, fontSize: 12, padding: '4px 0' }}>No event on record yet.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: 4, background: busSeverityColor(event.severity) }} />
            <span style={{ color: COLORS.textMuted, fontSize: 10, letterSpacing: 0.4, textTransform: 'uppercase' }}>
              {event.severity} · {event.category}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ color: COLORS.textMuted, fontSize: 10 }}>{relativeTime(event.created_at)}</span>
          </div>
          <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 500, marginBottom: 4,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}
               title={event.title}>
            {event.title}
          </div>
          {event.description && (
            <div style={{ color: COLORS.textSecondary, fontSize: 11, lineHeight: 1.5,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }}
                 title={event.description}>
              {event.description}
            </div>
          )}
          {!event.resolved && (
            <div style={{ color: COLORS.warn, fontSize: 10, marginTop: 6, fontWeight: 600, letterSpacing: 0.4 }}>UNRESOLVED</div>
          )}
        </>
      )}
    </div>
  );
}

function pickLatest(events: FounderOpsEvent[], pred: (e: FounderOpsEvent) => boolean): FounderOpsEvent | null {
  for (const e of events) if (pred(e)) return e;
  return null;
}

function googleStatusHint(e: FounderOpsEvent | null): string | undefined {
  if (!e) return undefined;
  if (e.type === 'google_api_pending')  return 'awaiting Google';
  if (e.type === 'google_api_approved') return 'approved';
  if (e.type === 'google_api_error')    return 'error';
  return e.severity;
}

// ── Archivist health card (Phase 6I) ───────────────────────────────────────
//
// Reads `founder-archivist-health` on mount and renders a compact status
// row: a health-label pill, latest-check age, and four counters lifted
// straight from the RPC envelope. Safe-fail: when the endpoint errors the
// card degrades to a one-liner note; the rest of Mission Control stays
// unaffected. No layout reshuffle above or below this card.

function ArchivistHealthCard({
  state, onRefresh,
}: {
  state: ArchivistHealthState;
  onRefresh: () => void;
}) {
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 10 }}>
        <span style={{ width: 3, height: 16, background: COLORS.textMuted, borderRadius: 2 }} />
        <span style={{ color: COLORS.text, fontSize: 16, fontWeight: 600 }}>Archivist Health</span>
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={onRefresh}>Refresh health</Button>
      </div>
      <Card>
        <ArchivistHealthBody state={state} />
      </Card>
    </div>
  );
}

function ArchivistHealthBody({ state }: { state: ArchivistHealthState }) {
  if (state.status === 'loading') {
    return <div style={{ color: COLORS.textMuted, fontSize: 13, padding: '4px 0' }}>Loading Archivist health…</div>;
  }
  if (state.status === 'error') {
    return (
      <div style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 1.5 }}>
        Archivist health endpoint unavailable. Mission Control above is unaffected.
        <div style={{ fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>{state.message}</div>
      </div>
    );
  }
  const p = state.payload;
  const c = p.summary_counts ?? {};
  return (
    <>
      <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap' }}>
        <ArchivistHealthPill health={p.health} />
        <span style={{ color: COLORS.textMuted, fontSize: 11 }}>
          Latest check {formatSecondsAgo(p.latest_check_age_seconds)}
          {p.latest_check_severity ? ` · severity ${p.latest_check_severity}` : ''}
          {typeof p.open_journal_count === 'number' ? ` · ${p.open_journal_count} open journal` : ''}
        </span>
      </div>
      <div style={archivistStatsGridStyle}>
        <ArchivistStatCell label="Missing daily summaries"   value={c.missing_daily_narratives ?? 0} />
        <ArchivistStatCell label="Missing facts extractions" value={c.missing_facts_extraction ?? 0} />
        <ArchivistStatCell label="Failed archivist reports"  value={c.failed_archivist_reports ?? 0} />
        <ArchivistStatCell label="Stale cron runs"           value={c.stale_cron_runs           ?? 0} />
      </div>
    </>
  );
}

function ArchivistHealthPill({ health }: { health: ArchivistHealthLevel }) {
  const cfg: Record<ArchivistHealthLevel, { label: string; color: string }> = {
    healthy:   { label: 'Healthy',   color: COLORS.success },
    watch:     { label: 'Watch',     color: COLORS.warn },
    degraded:  { label: 'Degraded',  color: '#F78B4B' },
    unhealthy: { label: 'Unhealthy', color: COLORS.critical },
    unknown:   { label: 'Unknown',   color: COLORS.textMuted },
  };
  const c = cfg[health] ?? cfg.unknown;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: RADIUS.chip,
      background: c.color + '1a', color: c.color, border: `1px solid ${c.color}55`,
      fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>{c.label}</span>
  );
}

function ArchivistStatCell({ label, value }: { label: string; value: number }) {
  const color = value > 0 ? COLORS.warn : COLORS.text;
  return (
    <div style={archivistStatCellStyle}>
      <div style={{ color, fontSize: 18, fontWeight: 600 }}>{num(value)}</div>
      <div style={{ color: COLORS.textMuted, fontSize: 10, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function formatSecondsAgo(seconds: number | null | undefined): string {
  if (seconds == null) return '(never)';
  if (seconds < 60)   return 'just now';
  const min = Math.floor(seconds / 60);
  if (min < 60)  return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)   return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

// ── Ava error boundary ─────────────────────────────────────────────────────
//
// React unmounts an entire subtree on an uncaught render error. Without a
// boundary in place, a crash inside AvaBriefBody (malformed cached payload,
// unexpected shape, etc.) would blank the whole Mission Control page.
// Wrapping the Ava card in this boundary keeps the deterministic Mission
// Control snapshot rendering below regardless of what Ava does.

interface AvaErrorBoundaryState { hasError: boolean; message: string | null }

class AvaErrorBoundary extends Component<{ children: ReactNode }, AvaErrorBoundaryState> {
  state: AvaErrorBoundaryState = { hasError: false, message: null };

  static getDerivedStateFromError(error: Error): AvaErrorBoundaryState {
    return { hasError: true, message: error.message ?? 'render_error' };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log for browser console visibility; never re-throw.
    console.error('[MissionControl] Ava card render crash:', error, info.componentStack);
    // Also promote to a Sentry event. React error boundaries swallow the
    // error before it hits window.onerror, so without an explicit capture
    // here the boundary crash would only appear as a breadcrumb, not an
    // Issue. No-op when VITE_SENTRY_DSN is unset.
    captureError(error, 'AvaErrorBoundary');
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{
          marginBottom: 16, padding: 20,
          background: '#111318', borderRadius: 12,
          border: `1px solid #22262E`, color: '#B0B6C1', fontSize: 13, lineHeight: 1.5,
        }}>
          <div style={{ color: '#E7E9EE', fontWeight: 600, marginBottom: 6 }}>
            Ava brief unavailable
          </div>
          Ava crashed while rendering. Mission Control below is unaffected.
          <div style={{ fontSize: 11, marginTop: 6, fontStyle: 'italic', color: '#7A8290' }}>
            {this.state.message}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Ava brief card ─────────────────────────────────────────────────────────

function AvaBriefCard({
  state, onRefresh, onAsk,
}: {
  state: AvaBriefState;
  onRefresh: () => void;
  onAsk: () => void;
}) {
  return (
    <Card style={{ marginBottom: 16, padding: 24, borderColor: COLORS.accent + '55' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ color: COLORS.accent, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 700 }}>Ava</span>
          <span style={{ color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.4, textTransform: 'uppercase' }}>Chief of Staff</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" onClick={onAsk}>Ask Ava</Button>
          <Button variant="ghost" onClick={onRefresh}>Refresh brief</Button>
        </div>
      </div>

      {state.status === 'loading' && (
        <div style={{ color: COLORS.textMuted, fontSize: 13, padding: '8px 0' }}>
          Ava is composing the briefing…
        </div>
      )}
      {state.status === 'error' && (
        <div style={{ color: COLORS.textMuted, fontSize: 13, lineHeight: 1.5 }}>
          Ava brief unavailable. Deterministic executive snapshot is still available below.
          <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
            {state.message}
          </div>
        </div>
      )}
      {state.status === 'idle' && (
        <div style={{ color: COLORS.textMuted, fontSize: 13, padding: '8px 0' }}>
          Waiting on Mission Control data…
        </div>
      )}
      {state.status === 'ready' && <AvaBriefBody brief={state.brief} />}
    </Card>
  );
}

function AvaBriefBody({ brief }: { brief: AvaBriefResponse }) {
  // Coerce every renderable array to a real array so a missing / null field
  // from any server variant (cached path, legacy row, future response drift)
  // never crashes AvaBriefBody with `undefined.length`.
  const immediateAttention = Array.isArray(brief.immediateAttention) ? brief.immediateAttention : [];
  const recommendedActions = Array.isArray(brief.recommendedActions) ? brief.recommendedActions : [];
  const risks              = Array.isArray(brief.risks)              ? brief.risks              : [];
  const unknowns           = Array.isArray(brief.unknowns)           ? brief.unknowns           : [];
  return (
    <div>
      <div style={{ color: COLORS.text, fontSize: 20, fontWeight: 600, letterSpacing: -0.2, marginBottom: 8 }}>
        {brief.briefTitle}
      </div>
      <div style={{ color: COLORS.textSecondary, fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
        {brief.summary}
      </div>

      {immediateAttention.length > 0 && (
        <AvaList label="Immediate attention" color={COLORS.critical} items={immediateAttention} />
      )}
      {recommendedActions.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={avaListLabel}>Recommended actions</div>
          {recommendedActions.map((a, i) => (
            <div key={i} style={avaActionRow}>
              <div style={{ ...urgencyPill(a.urgency), marginRight: 12 }}>{String(a.urgency ?? 'later').replace('_', ' ')}</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 500 }}>{a.title}</div>
                <div style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 2, lineHeight: 1.5 }}>
                  {a.reason} <span style={{ color: COLORS.textMuted }}>· {a.estimatedEffort} · {a.confidence}% confident</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {risks.length > 0 && (
        <AvaList label="Risks" color={COLORS.warn} items={risks} />
      )}
      {unknowns.length > 0 && (
        <AvaList label="Unknowns" color={COLORS.textMuted} items={unknowns} />
      )}

      <div style={avaFooter}>
        <span>Overall status: <b style={{ color: COLORS.text }}>{brief.overallStatus}</b></span>
        <span>Confidence: <b style={{ color: COLORS.text }}>{brief.confidence}%</b></span>
        <span>Reading time: {brief.readingTimeSeconds}s</span>
        <span>Generated: {relativeTime(brief.generatedAt)}</span>
        <span>Model: {brief.model}</span>
        {brief.cached && (
          <span style={{ color: COLORS.info }}>
            Cached · Refresh brief to spend a rate-limit slot on a fresh generation
          </span>
        )}
        {brief.memory_touched && (
          <span>
            Memory: {brief.memory_touched.timeline}t · {brief.memory_touched.journal}j · {brief.memory_touched.lessons}l · {brief.memory_touched.priorReports}a
          </span>
        )}
      </div>
    </div>
  );
}

function AvaList({ label, color, items }: { label: string; color: string; items: string[] }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={avaListLabel}>{label}</div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {items.map((it, i) => (
          <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '4px 0' }}>
            <span style={{ width: 6, height: 6, borderRadius: 3, background: color, marginTop: 8, flexShrink: 0 }} />
            <span style={{ color: COLORS.textSecondary, fontSize: 13, lineHeight: 1.5 }}>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function urgencyPill(u: unknown): React.CSSProperties {
  // Defensive: any legacy or malformed agent_reports row could reach here
  // with an unexpected `urgency`. Fall back to `later` styling rather than
  // crashing the whole Mission Control render tree.
  const styles = {
    now:        { bg: COLORS.critical + '2a', color: COLORS.critical },
    today:      { bg: '#F78B4B22',            color: '#F78B4B' },
    this_week:  { bg: COLORS.warn + '22',      color: COLORS.warn },
    later:      { bg: COLORS.textMuted + '22', color: COLORS.textMuted },
  } as const;
  const map = (typeof u === 'string' && u in styles)
    ? styles[u as keyof typeof styles]
    : styles.later;
  return {
    display: 'inline-block', minWidth: 76, textAlign: 'center' as const,
    padding: '3px 8px', borderRadius: RADIUS.chip,
    background: map.bg, color: map.color,
    fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' as const,
  };
}

// ── Ask Ava modal ──────────────────────────────────────────────────────────

function AskAvaModal({
  value, onChange, onSubmit, onCancel,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <div style={modalOverlayStyle} onClick={onCancel}>
      <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ color: COLORS.text, fontSize: 18, fontWeight: 600, marginBottom: 6 }}>Ask Ava</div>
        <div style={{ color: COLORS.textMuted, fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
          One question, 500 characters max. Ava will answer using the same Mission Control data you see now
          plus operational memory (timeline, journal, lessons).
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, 500))}
          placeholder="e.g. What should I focus on before the next EAS build?"
          rows={4}
          style={{
            width: '100%', background: COLORS.surfaceAlt, color: COLORS.text,
            border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.input,
            padding: 12, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' as const,
          }}
          autoFocus
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
          <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{value.length}/500</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button onClick={onSubmit} disabled={value.trim().length === 0}>Ask</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Incident state pill (Phase 4E) ─────────────────────────────────────────

function IncidentStatePill({ state }: { state: IncidentState }) {
  const config: Record<IncidentState, { label: string; color: string; bg: string }> = {
    none:               { label: 'No incident',               color: COLORS.success,    bg: COLORS.success + '1a' },
    watch:              { label: 'Watch condition',           color: COLORS.warn,       bg: COLORS.warn + '1a' },
    candidate:          { label: 'Incident candidate',        color: '#F78B4B',         bg: '#F78B4B1a' },
    critical_candidate: { label: 'Critical incident candidate', color: COLORS.critical, bg: COLORS.critical + '1a' },
  };
  const c = config[state];
  return (
    <span style={{
      display: 'inline-block', padding: '4px 10px', borderRadius: RADIUS.chip,
      background: c.bg, color: c.color, border: `1px solid ${c.color}55`,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
    }}>{c.label}</span>
  );
}

// ── Building blocks ────────────────────────────────────────────────────────

function SectionBlock({ title, accent, children }: { title: string; accent: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 10 }}>
        <span style={{ width: 3, height: 16, background: accent, borderRadius: 2 }} />
        <span style={{ color: COLORS.text, fontSize: 16, fontWeight: 600 }}>{title}</span>
      </div>
      <Card>{children}</Card>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ color: COLORS.textMuted, fontSize: 13, padding: '4px 0' }}>{children}</div>;
}

// ── Derivations ────────────────────────────────────────────────────────────

function collectWins(snap: Snapshot, briefs: Brief[]): string[] {
  const wins: string[] = [];
  const today = snap.today ?? {};
  if ((today.replies_generated ?? 0) > 0) {
    wins.push(`${today.replies_generated} replies generated today.`);
  }
  if ((today.event_count ?? 0) > 0 && (today.openai_failures ?? 0) === 0 && (today.google_failures ?? 0) === 0) {
    wins.push(`Zero OpenAI or Google failures across ${today.event_count} events today.`);
  }
  for (const b of briefs) {
    if (b.severity === 'healthy') {
      wins.push(`${b.name}: ${b.summary}`);
      if (wins.length >= 4) break;
    }
  }
  return wins;
}

function collectActions(briefs: Brief[]): Array<{ agent: string; name: string; action: string }> {
  const sorted = [...briefs].sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
  return sorted
    .filter((b) => b.severity !== 'healthy')
    .map((b) => ({ agent: b.id, name: b.name, action: b.action }));
}

function computeSystemStatus(p: MissionControlPayload): Array<{ system: string; color: string; detail: string }> {
  const findBrief = (id: string) => p.briefs.find((b) => b.id === id);
  const status = (id: string, label: string): { system: string; color: string; detail: string } => {
    const b = findBrief(id);
    if (!b) return { system: label, color: COLORS.textMuted, detail: 'no brief' };
    return {
      system: label,
      color:  severityColor(b.severity),
      detail: b.severityLabel,
    };
  };
  return [
    status('forge',     'Infrastructure'),
    status('sentinel',  'Security'),
    { system: 'AI',      color: (p.snapshot.today?.openai_failures ?? 0) > 0 ? COLORS.warn : COLORS.success, detail: `${p.snapshot.today?.replies_generated ?? 0} replies today` },
    { system: 'Google',  color: (p.snapshot.today?.google_failures ?? 0) > 0 ? COLORS.warn : COLORS.success, detail: p.snapshot.last_google_sync ? `sync ${relativeTime(p.snapshot.last_google_sync)}` : 'no syncs yet' },
    status('ledger',    'Billing'),
  ];
}

function severityColor(s: string): string {
  switch (s) {
    case 'healthy':          return COLORS.success;
    case 'watch':            return COLORS.warn;
    case 'needs_attention':  return '#F78B4B';
    case 'critical':         return COLORS.critical;
    case 'no_data':          return COLORS.textMuted;
    default:                 return COLORS.textMuted;
  }
}
function severityFromString(s: string): string {
  const u = s.toUpperCase();
  if (u === 'CRITICAL') return COLORS.critical;
  if (u === 'HIGH' || u === 'WARNING') return COLORS.warn;
  if (u === 'INFO')     return COLORS.info;
  return COLORS.textMuted;
}
function severityRank(s: Brief['severity']): number {
  switch (s) {
    case 'critical':         return 0;
    case 'needs_attention':  return 1;
    case 'watch':            return 2;
    case 'no_data':          return 3;
    case 'healthy':          return 4;
  }
}

function greetingTime(): string {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}
function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 5)  return 'Late night briefing';
  if (h < 12) return 'Morning briefing';
  if (h < 18) return 'Afternoon briefing';
  return 'Evening briefing';
}
function capitalise(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

// ── Styles ────────────────────────────────────────────────────────────────

const loadingStyle: React.CSSProperties = { padding: 40, color: COLORS.textMuted };
const errorStyle:   React.CSSProperties = { padding: 40, color: COLORS.critical, display: 'flex', alignItems: 'center', gap: 12 };
const listStyle:    React.CSSProperties = { margin: 0, padding: 0, listStyle: 'none' };
const listItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0',
  borderBottom: `1px dashed ${COLORS.border}`,
};
const dotStyle: React.CSSProperties = {
  width: 8, height: 8, borderRadius: 4, flexShrink: 0, marginTop: 6,
};
const timelineRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0',
  borderBottom: `1px dashed ${COLORS.border}`,
};
const statusGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10,
};
const statusPillStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', padding: '10px 12px',
  background: COLORS.surfaceAlt, borderRadius: RADIUS.input,
  border: `1px solid ${COLORS.border}`,
};
const teamGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12,
};
const teamCardStyle: React.CSSProperties = {
  background: COLORS.surfaceAlt, borderRadius: RADIUS.card,
  border: `1px solid ${COLORS.border}`, padding: 14,
};
const footerRowStyle: React.CSSProperties = {
  marginTop: 28, padding: '14px 0', borderTop: `1px solid ${COLORS.border}`,
  display: 'flex', flexWrap: 'wrap', gap: 20,
  color: COLORS.textMuted, fontSize: 12,
};

const avaListLabel: React.CSSProperties = {
  color: COLORS.textMuted, fontSize: 10, letterSpacing: 0.6,
  textTransform: 'uppercase', fontWeight: 700, marginBottom: 6,
};
const avaActionRow: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start',
  padding: '8px 0', borderBottom: `1px dashed ${COLORS.border}`,
};
const avaFooter: React.CSSProperties = {
  marginTop: 16, paddingTop: 12, borderTop: `1px solid ${COLORS.border}`,
  display: 'flex', flexWrap: 'wrap' as const, gap: 16,
  color: COLORS.textMuted, fontSize: 11,
};
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed' as const, inset: 0,
  background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 24,
};
const modalCardStyle: React.CSSProperties = {
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.card, padding: 24, width: '100%', maxWidth: 520,
  boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
};

const eventCardsGrid: React.CSSProperties = {
  display: 'grid', marginTop: 24,
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12,
};
const eventSingleCardStyle: React.CSSProperties = {
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADIUS.card, padding: 14, minHeight: 132,
};
const eventCardHeader: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8,
};
const eventCardHeaderTitle: React.CSSProperties = {
  color: COLORS.textMuted, fontSize: 10, letterSpacing: 0.6, textTransform: 'uppercase', fontWeight: 700,
};

const archivistStatsGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8,
};
const archivistStatCellStyle: React.CSSProperties = {
  background: COLORS.surfaceAlt, borderRadius: RADIUS.input,
  border: `1px solid ${COLORS.border}`, padding: '10px 12px',
};
